import { Component, DestroyRef, inject, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslateModule } from '@ngx-translate/core';

import { Globals } from '@core/globals';
import { ButtonComponent } from '@atoms/button/button.component';
import { ColorTextComponent } from '@atoms/color-text/color-text.component';
import { FormComponent } from '@atoms/form/form.component';
import { GridComponent } from '@atoms/grid/grid.component';
import { IconComponent } from '@atoms/icon/icon.component';
import { InputComponent } from '@atoms/input/input.component';
import { LoaderComponent } from '@atoms/loader/loader.component';
import { ParagraphComponent } from '@atoms/paragraph/paragraph.component';
import { PhoneInputComponent } from '@atoms/phone-input/phone-input.component';
import { SwitchComponent } from '@atoms/switch/switch.component';
import { TitleComponent } from '@atoms/title/title.component';
import { HidePhonePipe } from '@pipes/hide-phone.pipe';
import { FinancingFormService } from '@services/financing-form.service';
import { GlobalsService } from '@services/globals.service';
import { GtmService } from '@services/gtm.service';
import { LanguageService } from '@services/language.service';
import { ModalService } from '@services/modal.service';
import { SsrService } from '@services/ssr.service';
import { SaveFormService } from '@services/save-form.service';

@Component({
  selector: 'app-save-form',
  imports: [FormsModule, ReactiveFormsModule, TranslateModule, ButtonComponent, ColorTextComponent, FormComponent, GridComponent, IconComponent, InputComponent, LoaderComponent, ParagraphComponent, SwitchComponent, TitleComponent, HidePhonePipe, PhoneInputComponent],
  templateUrl: './save-form.component.html',
  styleUrl: './save-form.component.scss',
})
export class SaveFormComponent {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private globals = inject(Globals);
  public globalsService = inject(GlobalsService);
  public financingFormService = inject(FinancingFormService);
  private modalService = inject(ModalService);
  private ssrService = inject(SsrService);
  private gtmService = inject(GtmService);
  private languageService = inject(LanguageService);
  private saveFormService = inject(SaveFormService);
  @Input('verify-again') verifyAgain: boolean = false;
  public carId = this.activatedRoute.snapshot.queryParamMap.get('car') || '';
  private internalToken = this.activatedRoute.snapshot.queryParamMap.get('internal-token') || '';
  private id = this.activatedRoute.snapshot.queryParamMap.get('id') || '';
  public animate: boolean = false;
  public form = new FormGroup({
    type: new FormControl(false, { nonNullable: true, validators: [Validators.required] }), // false = Email, true = Phone
    email: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.email] }),
    phone: new FormControl('', { nonNullable: true, validators: [Validators.pattern(this.globals.regex.phone)] }),
    phoneCode: new FormControl('+420', { nonNullable: true, validators: [Validators.required] }),
  });

  send(): void {
    if (this.form.invalid) return;

    const isPhone = this.form.controls['type'].value;
    const email: string | undefined = !isPhone ? this.form.controls['email'].value : undefined;
    const phone: string | undefined = isPhone ? this.form.controls['phone'].value : undefined;
    const phoneCode: string | undefined = isPhone ? this.form.controls['phoneCode'].value : undefined;

    const data = {
      email,
      phone,
      phoneCode,
      type: isPhone,
      step: this.financingFormService.step(),
      verifyAgain: this.verifyAgain,
    };

    this.modalService.open('<app-confirm-modal [data]="' + this.globalsService.jsonToString(data) + '"></app-confirm-modal>', 'small');
  }

  openModalGtm(): void {
    const event = { event: 'click_calc_nastaveni_parametru_' + this.financingFormService.step() };
    this.gtmService.pushGtmPageType({ event: event });
    const dataLayer = {
      event: 'page_view',
      pageURL: '/' + this.languageService.language() + '/kalkulacka-nastaveni',
      pageType: 'Form-1',
      pageTitle: 'Kalkulačka',
    };
    this.gtmService.pushGtm(dataLayer);
  }

  ngOnInit(): void {
    const currentUrl = this.router.url;
    if (currentUrl.includes('nabidka-financovani')) {
      this.saveFormService.formType.set('financingForm');
    } else if (currentUrl.includes('oceneni-vozu')) {
      this.saveFormService.formType.set('carValuationForm');
    }
    if (this.internalToken) {
      this.router.navigate([this.globals.routes()['carValuationDetail'].url], { queryParams: { id: this.id, 'internal-token': this.internalToken } });
    }

    this.form.controls['type'].valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value: boolean) => {
      const emailControl = this.form.controls['email'];
      const phoneControl = this.form.controls['phone'];

      if (value === false) {
        emailControl.setValidators([Validators.required, Validators.email]);
        phoneControl.clearValidators();
        phoneControl.setValue('');
        phoneControl.markAsUntouched();
      } else {
        phoneControl.setValidators([Validators.required, Validators.pattern(this.globals.regex.phone)]);
        emailControl.clearValidators();
        emailControl.setValue('');
        emailControl.markAsUntouched();
      }

      emailControl.updateValueAndValidity();
      phoneControl.updateValueAndValidity();
    });
  }

  ngAfterViewInit(): void {
    if (this.ssrService.isServer()) return;
    setTimeout(() => {
      this.animate = true;
    }, 1);
  }
}
